import {
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import { IonContent } from '@ionic/angular';
import { ScrollDetail } from '@ionic/core';

import SunCalc from 'suncalc';

import {
  TERMINAL_ENABLED_TEMPORARY_DISABLED,
  TerminalDataModel,
  TranslationsLibService,
  ClimateEquipmentModel,
  DesktopClimateStatesStructureGroups,
  DesktopClimateStatesStructureModel,
  GroupsOrder,
  ClimateService,
  DesktopClimateMeteo,
  DesktopClimateModel,
  HelpersLibService,
  WeatherCalculateET0Request,
  WeatherService,
  WeatherPredictionDataModel,
} from '@nutricontrol/app360-shared';

import { environment } from '../../../../environments/environment';
import {
  generateMeteoGrid,
  saveDataAndShowModal,
} from '../../../commons/helpers/functions';
import { ClimateInstallerService } from '../../../farming/libraries/climate-installer.service';
import { ClimateMeteoService } from 'src/app/farming/libraries/climate-meteo.service';
import { Et0LibService } from 'src/app/farming/libraries/et0-lib.service';
@Component({
  selector: 'app-fields-dashboard-climate',
  templateUrl: './fields-dashboard-climate.component.html',
  styleUrls: ['./fields-dashboard-climate.component.scss'],
})
export class FieldsDashboardClimateComponent implements OnInit, OnDestroy {
  @Input() terminal: TerminalDataModel;
  @Input() equipment: ClimateEquipmentModel;

  public meteoVisibility: { key: string; visible: boolean }[] = [];
  public isFetchingStates = false;
  public meteoArray: any[];
  public calculateET0Request: WeatherCalculateET0Request;
  public isFetchingEquipmentData = false;
  public meteoRows = [];

  private intervalId: any;
  private groupVisibility: { name: string; visible: boolean }[];
  private zoneVisibility: { [key: number]: boolean } = {};
  private propVisibility: {
    [key: number]: DesktopClimateStatesStructureGroups;
  } = {};
  private terminal_vid: string;
  private statesResponse: DesktopClimateModel;
  private meteoResponse: DesktopClimateMeteo;
  private meteoStatesStructureArray: any[];
  private statesStructureResponse: DesktopClimateStatesStructureModel;
  private ignoreScrollEvent = false;
  private timeOutId: number;
  private weather: WeatherPredictionDataModel;
  private sunriseStr = '';
  private sunsetStr = '';
  private yesterday_ET0 = '-';
  public isFetchingET0 = false;

  @ViewChild(IonContent) content: IonContent;
  @ViewChild('tableElement') tableElement: ElementRef;

  protected readonly TERMINAL_ENABLED_TEMPORARY_DISABLED =
    TERMINAL_ENABLED_TEMPORARY_DISABLED;

  constructor(
    private climateService: ClimateService,
    private activatedRoute: ActivatedRoute,
    private helpersLib: HelpersLibService,
    public translationsLib: TranslationsLibService,
    private checkCurrentTabService: ClimateInstallerService,
    public climateMeteoService: ClimateMeteoService,
    public et0Service: Et0LibService,
    public weatherService: WeatherService
  ) {}

  handleScroll(ev: CustomEvent<ScrollDetail>) {
    if (!this.ignoreScrollEvent) {
      this.setLocalStorageScrollPosition(ev.detail.scrollTop.toString());
    } else {
      this.ignoreScrollEvent = false;
    }
  }

  ngOnInit() {
    this.terminal_vid = this.terminal.vid;

    if (this.terminal.enabled !== TERMINAL_ENABLED_TEMPORARY_DISABLED) {
      this.activatedRoute.params.subscribe(async (_) => {
        this.checkCurrentTabService.checkLocalStorageForCurrentTabIndex();
        this.checkCurrentTabService.removeInstallerOptionSelected();

        // Subscribe to route event to change title between route navigations
        this.activatedRoute.paramMap.subscribe((_) => {
          this.helpersLib.setTitle(
            '',
            this.equipment ? this.equipment.name : '',
            null
          );
        });

        await this.getHistoricWeather();
        await this.getWeatherCurrentByField();
        this.isFetchingEquipmentData = true;
        await this.postTerminalDataForVirtualInfo();
        this.isFetchingEquipmentData = false;

        await this.fetchDataAndUpdateTable();

        this.propagatePropFilterVisibility();

        this.intervalId = setInterval(() => {
          this.fetchDataAndUpdateTable();
        }, environment.intervalDefaultTimeout);
      });
    }
  }

  ngOnDestroy() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }

    if (this.timeOutId) {
      clearTimeout(this.timeOutId);
      this.timeOutId = null;
    }
  }

  async fetchDataAndUpdateTable() {
    try {
      const savedScrollPosition = this.getLocalStorageScrollPosition();
      const scrollPosition =
        savedScrollPosition !== null ? parseInt(savedScrollPosition, 10) : 0;

      await this.getStates();
      await this.getClimateStatesStructure();

      this.ignoreScrollEvent = true;

      if (this.content !== undefined)
        await this.content.scrollToPoint(0, scrollPosition);
    } catch (err) {
      throw new Error(err.message);
    }
  }

  async forceUpdate() {
    await this.fetchDataAndUpdateTable();
  }

  getStates(): Promise<boolean> {
    return new Promise<boolean>((resolve, _) => {
      try {
        this.isFetchingStates = true;
        this.climateService
          .getDesktopClimateStates(this.terminal_vid)
          .subscribe((res) => {
            this.statesResponse = res;
            this.meteoResponse = res.meteo;
            if (this.meteoResponse && this.meteoResponse.fields) {
              this.meteoArray = Object.keys(this.meteoResponse.fields).map(
                (key) => this.meteoResponse.fields[key]
              );
            }
            this.isFetchingStates = false;
            resolve(true);
          });
      } catch (err) {
        throw new Error(err.message);
      }
    });
  }

  getClimateStatesStructure(): Promise<boolean> {
    return new Promise<boolean>((resolve, _) => {
      try {
        this.climateService
          .getDesktopClimateStructureStates(this.terminal_vid)
          .subscribe((res) => {
            this.statesStructureResponse = res;
            if (
              this.statesStructureResponse &&
              this.statesStructureResponse.zones
            ) {
              this.statesStructureResponse.zones.forEach((zoneInfo) => {
                this.zoneVisibility[zoneInfo.zone] = zoneInfo.visible;
                this.propVisibility[0] = zoneInfo.groups;
              });
            }
            if (
              this.statesStructureResponse &&
              this.statesStructureResponse.meteo
            ) {
              this.meteoStatesStructureArray = Object.keys(
                this.statesStructureResponse.meteo.fields
              ).map((key) => this.statesStructureResponse.meteo.fields[key]);
            }
            this.meteoVisibility = this.meteoStatesStructureArray;
            if (
              this.statesStructureResponse &&
              this.statesStructureResponse.zones
            ) {
              this.groupVisibility = Object.keys(
                this.statesStructureResponse.zones[0].groups
              ).map((key) => ({
                name: key,
                visible: true,
              }));
            }
            resolve(true);
          });
      } catch (err) {
        throw new Error(err.message);
      }
    });
  }

  isGroupVisible(name: string): boolean {
    const foundGroup = this.groupVisibility.find((elem) => elem.name === name);
    return foundGroup ? foundGroup.visible : false;
  }

  getGroupVisibility(groupItem) {
    return this.groupVisibility.find((group) => group.name === groupItem.group)
      ?.visible;
  }

  propagatePropFilterVisibility() {
    const propVisibilityList: { key: string; isVisible: boolean }[] = [];

    this.getZoneKey().forEach((keysArr) =>
      keysArr.keys.forEach((key) =>
        propVisibilityList.push({ key, isVisible: true })
      )
    );

    return propVisibilityList;
  }

  toggleGroupVisibility(data) {
    const foundGroup = this.groupVisibility.find(
      (group) => group.name === data.group
    );
    if (foundGroup) {
      foundGroup.visible = !foundGroup.visible;
    }
  }

  trackByFn(index: number, item: any) {
    return item.key;
  }

  getZoneKey(): { group: string; keys: string[] }[] {
    const keysByGroup: { [key: string]: string[] } = {};
    if (this.statesResponse && this.statesResponse.zones) {
      for (const zone of this.statesResponse.zones) {
        for (const groupKey in zone.groups) {
          if (zone.groups.hasOwnProperty(groupKey)) {
            const group = zone.groups[groupKey];
            const zoneKey = Object.keys(group.fields);
            if (!keysByGroup[groupKey]) {
              keysByGroup[groupKey] = zoneKey;
            } else {
              keysByGroup[groupKey].push(...zoneKey);
            }
          }
        }
      }
    }

    const res = Object.keys(keysByGroup).map((groupKey) => ({
      group: groupKey,
      keys: Array.from(new Set(keysByGroup[groupKey])),
    }));

    return res.sort((a, b) => {
      return GroupsOrder.findIndex((group) => a.group === group) >
        GroupsOrder.findIndex((group) => b.group === group)
        ? 1
        : -1;
    });
  }

  getZoneValue(value: any, key: string): string | boolean {
    if (value) {
      for (const groupKey in value) {
        if (value.hasOwnProperty(groupKey)) {
          const fields = value[groupKey].fields;
          for (const fieldKey in fields) {
            if (fields.hasOwnProperty(fieldKey) && fieldKey === key) {
              const fieldValue = fields[fieldKey].value;
              const fieldUnit = fields[fieldKey].unit;

              const isFalse = fieldValue === 'false';
              const isNull = fieldValue === null;
              const isNumberOrNonEmptyString =
                typeof fieldValue === 'number' ||
                (typeof fieldValue === 'string' && fieldValue.length > 0);

              if (isFalse || isNull) {
                return '-';
              } else if (isNumberOrNonEmptyString) {
                return `${fieldValue} ${fieldUnit}`;
              }
            }
          }
        }
      }
    }
    return '-';
  }

  getKeyClass(item) {
    switch (item.toUpperCase()) {
      case 'VENT':
        return 'vent';
      case 'MISTING':
        return 'misting';
      case 'HEAT':
        return 'heat';
      case 'CURTAIN':
        return 'curtain';
      case 'HAFFAN':
        return 'haf_fans';
      case 'CO2':
        return 'co2';
      case 'SWITCHING':
        return 'switching_programs';
      case 'ALARM':
        return 'alarm';
      case 'COMMON':
        return 'common';
      default:
        return '';
    }
  }

  getValueClass(item) {
    switch (item.toUpperCase()) {
      case 'VENT':
        return 'vent-value';
      case 'MISTING':
        return 'misting-value';
      case 'HEAT':
        return 'heat-value';
      case 'CURTAIN':
        return 'curtain-value';
      case 'HAF_FANS':
        return 'haf_fans-value';
      case 'CO2':
        return 'co2-value';
      case 'SWITCHING_PROGRAMS':
        return 'switching_programs-value';
      case 'ALARM':
        return 'alarm-value';
      case 'COMMON':
        return 'common-value';
      default:
        return '';
    }
  }

  onPropChecked(event: { group: string; prop: string; value: boolean }): void {
    this.propVisibility[0][event.group].fields[event.prop].visible =
      event.value;
  }

  onZoneChecked(event: { zone: number; value: boolean }): void {
    this.zoneVisibility[event.zone] = event.value;
  }

  onMeteoChecked(event: { key: string; value: boolean }): void {
    this.meteoVisibility.forEach((meteo) => {
      if (meteo.key === event.key) meteo.visible = event.value;
    });
  }

  getVisibleMeteoKeys() {
    if (!this.meteoVisibility) {
      return [];
    }

    return this.meteoVisibility.filter((meteo) => meteo.visible === true);
  }

  getVisibleZones(): number[] {
    const visibleZones = Object.keys(this.zoneVisibility)
      .filter((zone) => this.zoneVisibility[zone])
      .map(Number);

    return visibleZones.length > 0
      ? visibleZones
      : this.statesResponse?.zones.map((zone) => zone.zone);
  }

  isVisibleZoneInMobile(zone: number): boolean {
    return this.getVisibleZones().includes(zone);
  }

  getVisibleProps(group: string): string[] {
    if (!this.propVisibility[0] || !this.propVisibility[0][group]) {
      return [];
    }

    const groupProps = this.propVisibility[0][group].fields;
    return Object.keys(groupProps)
      .filter((prop) => groupProps[prop].visible)
      .sort((a, b) => (a > b ? 1 : -1));
  }

  submitVisibility(event, showModal = true): Promise<boolean> {
    return new Promise<boolean>((resolve, _) => {
      try {
        this.climateService
          .postDesktopClimateStructureStates(this.terminal_vid, event)
          .subscribe((res) => {
            saveDataAndShowModal(
              res,
              showModal,
              this.translationsLib.get('error_updating_data'),
              this.translationsLib.get('save_changes_success'),
              this.translationsLib.get('accept')
            );

            resolve(true);
          });
      } catch (err) {
        throw new Error(err.message);
      }
    });
  }

  getLocalStorageScrollPosition(): string {
    return localStorage.getItem('scrollPosition');
  }

  setLocalStorageScrollPosition(tabIndex: string) {
    localStorage.setItem('scrollPosition', tabIndex);
  }

  public equipmentConnected(): number {
    return Number(this.equipment.connected);
  }

  private getHistoricWeather(): Promise<boolean> {
    return new Promise<boolean>((resolve, _) => {
      this.et0Service
        .getHistoricWeather(this.terminal)
        .then((result) => {
          this.calculateET0Request = result.calculateET0Request;
          resolve(true);
        })
        .catch((error) => {
          console.log(error);
          resolve(false);
        });
    });
  }

  getWeatherCurrentByField(): Promise<boolean> {
    return new Promise<boolean>((resolve, _) => {
      try {
        this.weatherService
          .getWeatherCurrentByField(this.terminal.field.vid)
          .subscribe((res) => {
            this.weather = res;

            resolve(true);
          });
      } catch (err) {
        throw new Error(err);
      }
    });
  }

  postTerminalDataForVirtualInfo(): Promise<boolean> {
    return new Promise<boolean>((resolve, _) => {
      try {
        const virtualInfo = {
          info: {
            meteo: {
              date: this.weather?.date,
              main_temp: this.weather?.main_temp,
              main_pressure: this.weather?.main_pressure,
              wind_speed: this.weather?.wind_speed,
              solar_rad: this.weather?.solar_rad,
              precip: this.weather?.precip,
              main_humidity: this.weather?.main_humidity,
              wind_direction: this.weather?.wind_deg,
            },
          },
        };
        this.climateService
          .postClimateTerminal(this.terminal_vid, virtualInfo)
          .subscribe(
            (res) => this.handleClimateTerminal(res, resolve),
            (error) => {
              resolve(false);
            }
          );
      } catch (err) {
        throw new Error(err);
      }
    });
  }

  private async handleClimateTerminal(
    res: ClimateEquipmentModel,
    resolve: (value: boolean) => void
  ): Promise<void> {
    this.equipment = res;

    // Sunrise / Sunset hours
    this.calculateSunriseSunsetHoursSunTimes();

    // Subscribe to route event to change title between route navigations
    this.activatedRoute.paramMap.subscribe((_) => {
      this.helpersLib.setTitle('', res.name, null);
    });

    // Sensor/Virtual meteo list
    await this.processSensorVirtualMeteoList();
    resolve(true);
  }

  private calculateSunriseSunsetHoursSunTimes(): void {
    if (
      !this.equipment.latitude ||
      !this.equipment.longitude ||
      !this.equipment.systemHour
    ) {
      this.sunriseStr = this.sunsetStr = '';
      return;
    }

    const times = SunCalc.getTimes(
      new Date(this.equipment.systemHour),
      this.equipment.latitude,
      this.equipment.longitude
    );
    this.sunriseStr =
      times.sunrise.getHours() +
      ':' +
      this.formatMinutes(times.sunrise.getMinutes());

    this.sunsetStr =
      times.sunset.getHours() +
      ':' +
      this.formatMinutes(times.sunset.getMinutes());
  }

  private async processSensorVirtualMeteoList(): Promise<void> {
    const { latitude, longitude } = this.equipment;

    if (this.terminal.field === null || (!latitude && !longitude)) {
      if (this.terminal.latitude !== null || this.terminal.longitude !== null) {
        await this.getCurrentWeatherByTerminal();
      }
      return;
    }

    await this.et0Service
      .getET0(this.calculateET0Request, this.equipment)
      .then((result) => {
        this.yesterday_ET0 = result.yesterday_ET0;
        this.isFetchingET0 = result.isFetchingET0;

        this.updateMeteoRows(this.equipment, this.calculateET0Request);
      });
  }

  updateMeteoRows(res: ClimateEquipmentModel, calculateET0Request: any) {
    if (res?.meteo === null || res?.meteo.length === 0) res = this.equipment;

    if (res.meteo instanceof Array && res.meteo.length === 0) {
      this.meteoRows = generateMeteoGrid([
        {
          key: this.translationsLib.get('climate_alarm_grid_ET0'),
          value: Number(this.yesterday_ET0),
          name: null,
          unit: 'mm',
          origin: null,
        },
      ]);
    } else {
      if (
        calculateET0Request !== undefined &&
        this.terminal.field_vid !== undefined
      ) {
        if (
          this.yesterday_ET0 !==
          this.translationsLib.get('climate_alarm_grid_ET0_na')
        ) {
          res.meteo.unshift({
            key: this.translationsLib.get('climate_alarm_grid_ET0'),
            value: Number(this.yesterday_ET0),
            name: this.translationsLib.get('climate_alarm_grid_ET0'),
            unit: this.translationsLib.get('climate_units_mm'),
            origin: null,
          });
        }
      }

      if (this.equipment.latitude && this.equipment.longitude) {
        res.meteo.push(
          {
            key: 'SUNRISE',
            value: this.sunriseStr,
            name: 'Hora de amanecer',
            unit: 'hh:mm',
            origin: null,
          },
          {
            key: 'SUNSET',
            value: this.sunsetStr,
            name: 'Hora de anochecer',
            unit: 'hh:mm',
            origin: null,
          }
        );
      }

      this.meteoRows = generateMeteoGrid(res.meteo);
    }
  }

  getCurrentWeatherByTerminal(): Promise<boolean> {
    return new Promise<boolean>((resolve, _) => {
      try {
        this.weatherService
          .getCurrentWeatherByTerminal(this.terminal_vid)
          .subscribe(
            (res) => {
              this.weather = res;

              // @ts-ignore
              if (res?.error) resolve(false);
              else resolve(true);
            },
            (error) => {
              console.error(error.status);
              resolve(false);
            }
          );
      } catch (err) {
        throw new Error(err);
      }
    });
  }

  formatMinutes = (minutes) => {
    return minutes < 10 ? '0' + minutes : '' + minutes;
  };

  public meteoPills() {
    return this.climateMeteoService
      .orderMeteoArray(this.meteoRows)
      .filter((meteo) => {
        return meteo.key !== 'SUNSET' && meteo.key !== 'SUNRISE';
      });
  }
}
